import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import MerchantApplicationModal from '../Modals/MerchantApplicationModal';
import AddSeriesModal from '../Webinars/AddSeriesModal';
import LocalProvidersModal from '../Modals/LocalProvidersModal';
import { useAuth } from '../../context/AuthContext';

const {
  FiFeather, FiMail, FiInstagram, FiTwitter, FiFacebook,
  FiChevronRight, FiCheck, FiBriefcase, FiVideo, FiCalendar, FiSearch,
} = FiIcons;

const Footer = () => {
  const [email, setEmail]                         = useState('');
  const [subscribed, setSubscribed]               = useState(false);
  const [isMerchantApplyOpen, setIsMerchantApplyOpen] = useState(false);
  const [isCreateWebinarOpen, setIsCreateWebinarOpen] = useState(false);
  const [isLocalProvidersOpen, setIsLocalProvidersOpen] = useState(false);

  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, permissions } = useAuth();

  const isLoggedIn     = !!user;
  const canHostWebinar = isLoggedIn && permissions?.canHostWebinar?.();

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setTimeout(() => setSubscribed(false), 3000);
      setEmail('');
    }
  };

  const navTo = (tab) => navigate(`/resources?tab=${encodeURIComponent(tab)}`);

  /* ── "Create Webinar" handler ─────────────────────────────────────────────
   * Opens the New Webinar Series modal for admins / creators / speakers.
   * For visitors the button is replaced with "Find Webinars" (→ /webinars).
   * ─────────────────────────────────────────────────────────────────────── */
  const handleCreateWebinar = () => setIsCreateWebinarOpen(true);

  const footerLinks = [
    {
      title: 'Explore',
      links: [
        { label: 'Find a Memorial', action: () => navigate('/explore') },
        { label: 'Find Local Provider', action: () => setIsLocalProvidersOpen(true) },
        { label: 'Live Broadcasts', action: () => navigate('/live-events') },
        { label: 'Gift Shop',       action: () => navigate('/marketplace') },
        { label: 'Merchant Portal', action: () => navigate('/merchant') },
        { label: 'Charity Partners',action: () => navTo('About') },
      ],
    },
    {
      title: 'Resources',
      links: [
        { label: 'Grief Support',     action: () => navTo('Support Groups') },
        { label: 'Obituary Writing',  action: () => navTo('Workshops') },
        { label: 'Funeral Planning',  action: () => navTo('Funeral Planning') },
        { label: 'Legal & Will Prep', action: () => navTo('Legal') },
        // Only show "Host Webinar" link in footer list for eligible roles
        ...(canHostWebinar
          ? [{ label: 'Host Webinar', action: handleCreateWebinar }]
          : []),
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'Our Story',        action: () => navTo('About') },
        { label: 'Privacy Policy',   action: () => navTo('Legal') },
        { label: 'Terms of Service', action: () => navTo('Legal') },
        { label: 'Contact Us',       action: () => navTo('About') },
      ],
    },
  ];

  /* Banner copy changes per role */
  const bannerTitle = canHostWebinar ? 'Host a Webinar Session' : 'Join a Webinar Session';
  const bannerBody  = canHostWebinar
    ? 'Share your expertise with our community. Host webinars on legacy planning, grief support, or memorial creation.'
    : 'Explore expert-led sessions on grief support, estate planning, and legacy preservation — free and paid options available.';
  const bannerBadge = canHostWebinar ? 'For Experts & Professionals' : 'Open to Everyone';

  /* Show banner ONLY on home page, /webinars, and /live-events */
  const showBanner = location.pathname === '/' || 
                     location.pathname.startsWith('/webinars') || 
                     location.pathname.startsWith('/live-events');

  return (
    <footer className="bg-white border-t border-brand-dark pt-20 pb-10">
      <div className="container mx-auto px-6">

        {/* ── Webinar Banner ── */}
        {showBanner && (
          <div className="mb-16">
            <div className="bg-accent-navy text-white rounded-[32px] p-8 md:p-10 shadow-xl flex flex-col md:flex-row justify-between items-center gap-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-accent-gold/10 rounded-full -mr-32 -mt-32 blur-3xl pointer-events-none" />

              <div className="relative z-10 max-w-xl">
                <span className="inline-block bg-accent-gold/20 text-accent-gold px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-accent-gold/20 mb-5">
                  {bannerBadge}
                </span>
                <h3 className="text-3xl font-serif mb-4">{bannerTitle}</h3>
                <p className="text-white/60 font-light">{bannerBody}</p>
              </div>

              <div className="relative z-10 flex-shrink-0 flex flex-col sm:flex-row gap-3">
                {canHostWebinar ? (
                  /* ── Admin / Creator / Speaker → open New Webinar Series modal ── */
                  <button
                    onClick={handleCreateWebinar}
                    className="inline-flex items-center justify-center gap-3 px-10 py-4 bg-accent-gold text-white font-black uppercase tracking-widest text-sm rounded-2xl shadow-lg shadow-accent-gold/20 hover:bg-accent-gold/90 transition-all"
                  >
                    <SafeIcon icon={FiVideo} /> Create Webinar
                  </button>
                ) : isLoggedIn ? (
                  /* ── Logged-in, no host role → view schedule ── */
                  <button
                    onClick={() => navigate('/webinars')}
                    className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white/10 border border-white/20 rounded-2xl text-white hover:bg-white/20 transition-all text-sm font-bold"
                  >
                    <SafeIcon icon={FiCalendar} /> View Schedule
                  </button>
                ) : (
                  /* ── Logged-out visitor → find / view schedule ── */
                  <>
                    <button
                      onClick={() => navigate('/webinars')}
                      className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-accent-gold text-white font-black uppercase tracking-widest text-sm rounded-2xl shadow-lg shadow-accent-gold/20 hover:bg-accent-gold/90 transition-all"
                    >
                      <SafeIcon icon={FiSearch} /> Find Webinars
                    </button>
                    <button
                      onClick={() => navigate('/webinars')}
                      className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white/10 border border-white/20 rounded-2xl text-white hover:bg-white/20 transition-all text-sm font-bold"
                    >
                      <SafeIcon icon={FiCalendar} /> View Schedule
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Link columns ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-20">
          <div className="lg:col-span-2 space-y-6">
            <Link to="/" className="flex items-center gap-2 font-serif text-2xl text-accent-navy">
              <SafeIcon icon={FiFeather} className="text-accent-gold w-8 h-8" />
              <span className="font-bold">OneFinalLegacy</span>
            </Link>
            <p className="text-text-muted text-lg font-light leading-relaxed">
              Creating a digital sanctuary where legacies are honored, stories are shared, and memories live on forever.
            </p>
            <div className="flex gap-4">
              <SocialIcon icon={FiInstagram} href="https://instagram.com" />
              <SocialIcon icon={FiTwitter}   href="https://twitter.com" />
              <SocialIcon icon={FiFacebook}  href="https://facebook.com" />
            </div>
            <div className="pt-4">
              <button
                onClick={() => setIsMerchantApplyOpen(true)}
                className="group flex flex-col p-6 bg-brand-light rounded-3xl border border-brand-dark hover:border-accent-gold transition-all text-left w-full"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-accent-navy text-white rounded-xl flex items-center justify-center group-hover:bg-accent-gold transition-colors">
                    <SafeIcon icon={FiBriefcase} />
                  </div>
                  <h5 className="font-serif text-lg text-accent-navy">Sell with us</h5>
                </div>
                <p className="text-xs text-text-muted font-light">
                  Join our curated marketplace of memorial artisans and services.
                </p>
              </button>
            </div>
          </div>

          {footerLinks.map((section) => (
            <div key={section.title}>
              <h4 className="font-serif text-xl text-accent-navy mb-6">{section.title}</h4>
              <ul className="space-y-4">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <button
                      onClick={link.action}
                      className="text-text-muted hover:text-accent-gold transition-colors flex items-center gap-1 group text-left"
                    >
                      <SafeIcon icon={FiChevronRight} className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-all -ml-4 group-hover:ml-0" />
                      {link.label}
                    </button>
                  </li>
                ))}
                {section.title === 'Company' && (
                  <li>
                    <button
                      onClick={() => setIsMerchantApplyOpen(true)}
                      className="text-text-muted hover:text-accent-gold transition-colors flex items-center gap-1 group"
                    >
                      <SafeIcon icon={FiChevronRight} className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-all -ml-4 group-hover:ml-0" />
                      Become a Merchant
                    </button>
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>

        {/* ── Newsletter ── */}
        <div className="bg-brand-light rounded-3xl p-8 md:p-12 mb-20 flex flex-col lg:flex-row items-center justify-between gap-10">
          <div className="text-center lg:text-left">
            <h3 className="text-2xl md:text-3xl font-serif text-accent-navy mb-2">Join our compassionate community</h3>
            <p className="text-text-muted font-light">Get monthly resources on honoring loved ones and managing grief.</p>
          </div>
          <form onSubmit={handleSubscribe} className="w-full lg:w-auto flex flex-col sm:flex-row gap-4 max-w-full">
            <div className="relative flex-grow w-full sm:min-w-[300px]">
              <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
              <input
                type="email"
                placeholder="Your email address"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-6 py-4 rounded-xl border border-brand-dark focus:ring-2 focus:ring-accent-gold/20 outline-none text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={subscribed}
              className={`px-8 py-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 w-full sm:w-auto ${subscribed ? 'bg-green-500 text-white' : 'bg-accent-navy text-white hover:bg-accent-navy/90'}`}
            >
              {subscribed ? <><SafeIcon icon={FiCheck} /> Subscribed</> : 'Join Now'}
            </button>
          </form>
        </div>

        {/* ── Bottom bar ── */}
        <div className="flex flex-col md:flex-row justify-between items-center pt-10 border-t border-brand-dark text-sm text-text-muted">
          <p>© {new Date().getFullYear()} OneFinalLegacy. All rights reserved.</p>
          <div className="flex gap-8 mt-4 md:mt-0">
            <button onClick={() => navTo('Legal')} className="hover:text-accent-gold transition-colors">Privacy</button>
            <button onClick={() => navTo('Legal')} className="hover:text-accent-gold transition-colors">Terms</button>
            <button onClick={() => navTo('About')} className="hover:text-accent-gold transition-colors">Cookies</button>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      <MerchantApplicationModal isOpen={isMerchantApplyOpen} onClose={() => setIsMerchantApplyOpen(false)} />
      <LocalProvidersModal isOpen={isLocalProvidersOpen} onClose={() => setIsLocalProvidersOpen(false)} />

      <AnimatePresence>
        {isCreateWebinarOpen && (
          <AddSeriesModal
            onClose={() => setIsCreateWebinarOpen(false)}
            onSave={(newWebinar) => {
              // In a full implementation this would push to the global webinar store.
              console.info('[Footer] New webinar created:', newWebinar);
              setIsCreateWebinarOpen(false);
            }}
          />
        )}
      </AnimatePresence>
    </footer>
  );
};

const SocialIcon = ({ icon, href }) => (
  <a href={href} target="_blank" rel="noopener noreferrer"
    className="w-10 h-10 rounded-full bg-brand-light flex items-center justify-center text-accent-navy hover:bg-accent-gold hover:text-white transition-all shadow-sm">
    <SafeIcon icon={icon} className="w-5 h-5" />
  </a>
);

export default Footer;